import { useState, useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { State } from 'ducks/editor/objects'
import {
  setAuditData,
  getAuditData,
  AuditData,
  clearAuditData,
} from 'ducks/x-ray'
import { requestAudit } from 'utils/io/x-ray'

// Preload lazy components to avoid loading states after audit completes
const preloadLazyComponents = async () => {
  try {
    await Promise.all([
      import('../components/NestedListsSection'),
      import('../components/ComplexitySection'),
      import('../components/CacheHitRatiosSection'),
      import('../components/OrphanedScreensSection'),
      import('../components/LiveRowCountsSection'),
      import('../components/ComponentsSection'),
    ])
  } catch (error) {
    console.warn('Failed to preload some components:', error)
  }
}

interface UseAuditDataReturn {
  auditData: AuditData | null
  isAuditing: boolean
  isLoading: boolean
  isRefreshing: boolean
  error: string | null
  progress: number
  fetchAuditData: (isRefresh?: boolean) => Promise<void>
  setIsAuditing: (auditing: boolean) => void
}

export const useAuditData = (appId: string): UseAuditDataReturn => {
  const dispatch = useDispatch()
  const auditData = useSelector((state: State) => getAuditData(state, appId))

  const [isAuditing, setIsAuditing] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [progress, setProgress] = useState<number>(0.01)

  // Ref to track if component is mounted to prevent state updates on unmounted component
  const isMountedRef = useRef(true)

  // Ref to store abort controller for cleanup
  const abortControllerRef = useRef<AbortController | null>(null)

  // Effect to check for existing data and auto-start auditing
  useEffect(() => {
    if (auditData) {
      setIsAuditing(true)
      setIsRefreshing(false)
    }
  }, [auditData])

  // Cleanup effect
  useEffect(() => {
    return () => {
      isMountedRef.current = false
      // Abort any ongoing requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  const fetchAuditData = useCallback(
    async (isRefresh = false) => {
      // Abort any existing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController()

      // Set loading state immediately
      if (isRefresh) {
        setIsRefreshing(true)
      } else {
        setIsAuditing(true)
        setIsLoading(true)
      }

      // Don't fetch if we already have data and it's not a refresh
      if (auditData && !isRefresh) {
        setIsAuditing(true)
        setIsLoading(false)

        return
      }

      if (isRefresh) {
        dispatch(clearAuditData(appId))
      }

      setError(null)

      try {
        // Start preloading components while audit is running
        const preloadPromise = preloadLazyComponents()

        // Request the audit
        const response = await requestAudit({
          appId,
          onProgress: (chunkProgress: number) => {
            setProgress(chunkProgress)
          },
          onError: (errorMessage: string) => {
            // Handle error via callback - set error state in React
            if (isMountedRef.current) {
              setError(errorMessage)
              setIsLoading(false)
              setIsRefreshing(false)
              setProgress(0.01)
            }
          },
        })

        // Wait for both audit and component preloading to complete
        await preloadPromise

        // Only update state if component is still mounted
        if (isMountedRef.current) {
          // Set the audit data
          dispatch(setAuditData(appId, response as AuditData))
        }
      } catch (err) {
        // console.log('err', err)
        // Only handle error if component is still mounted
        if (isMountedRef.current) {
          // console.error('Error performing audit:', err)
          setError('Failed to perform audit. Please try again.')
        }
      } finally {
        // Only update loading state if component is still mounted
        if (isMountedRef.current) {
          setIsLoading(false)
          setIsRefreshing(false)
          setProgress(0.01)
        }
      }
    },
    [appId, auditData, dispatch]
  )

  return {
    auditData,
    isAuditing,
    isLoading,
    isRefreshing,
    error,
    progress,
    fetchAuditData,
    setIsAuditing,
  }
}
